/**
 * useDraggablePanel.js — hooks/useDraggablePanel.js
 *
 * Enables drag (via header) and resize (via corner grip) for desktop panels.
 * DISABLED on mobile/tablet (screen width <= 1099px) — panels become static
 * stacked cards via CSS media queries on those screen sizes.
 *
 * Uses window-level pointermove/pointerup so dragging doesn't break when
 * the cursor moves faster than the element.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** True if screen is too small for draggable panels */
function isMobile() {
  return window.innerWidth <= 1099;
}

export function useDraggablePanel(initialPos, initialSize, onChangeEnd) {
  const [pos,        setPos]        = useState(initialPos);
  const [size,       setSize]       = useState(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const panelRef  = useRef(null);
  const headerRef = useRef(null);
  const resizeRef = useRef(null);
  const stateRef  = useRef(null);
  const posRef    = useRef(pos);
  const sizeRef   = useRef(size);

  // Keep refs in sync with state so event handlers always read current values
  useEffect(() => { posRef.current  = pos;  }, [pos]);
  useEffect(() => { sizeRef.current = size; }, [size]);

  // ── Shared window handlers (registered during a drag/resize) ──────────────
  const onWindowMove = useCallback((e) => {
    const d = stateRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (d.type === 'drag') {
      setPos({ x: Math.max(0, d.origX + dx), y: Math.max(0, d.origY + dy) });
    } else {
      setSize({ w: Math.max(220, d.origW + dx), h: Math.max(120, d.origH + dy) });
    }
  }, []);

  const onWindowUp = useCallback(() => {
    if (!stateRef.current) return;
    const type = stateRef.current.type;
    stateRef.current = null;
    setIsDragging(false);
    setIsResizing(false);
    window.removeEventListener('pointermove', onWindowMove);
    window.removeEventListener('pointerup',   onWindowUp);
    // Notify parent of final position/size after interaction ends
    if (onChangeEnd) {
      // Use refs to get the latest values synchronously
      onChangeEnd({ pos: posRef.current, size: sizeRef.current });
    }
  }, [onWindowMove, onChangeEnd]);

  // ── Header — drag ─────────────────────────────────────────────────────────
  useEffect(() => {
    const hdr = headerRef.current;
    if (!hdr) return;
    const onDown = (e) => {
      if (e.button !== 0 || isMobile()) return; // no drag on mobile
      e.preventDefault();
      const { x, y } = posRef.current;
      stateRef.current = { type: 'drag', startX: e.clientX, startY: e.clientY, origX: x, origY: y };
      setIsDragging(true);
      if (panelRef.current) panelRef.current.style.zIndex = nextZ();
      window.addEventListener('pointermove', onWindowMove);
      window.addEventListener('pointerup',   onWindowUp);
    };
    hdr.addEventListener('pointerdown', onDown);
    return () => hdr.removeEventListener('pointerdown', onDown);
  }, [onWindowMove, onWindowUp]);

  // ── Resize handle ─────────────────────────────────────────────────────────
  useEffect(() => {
    const rsz = resizeRef.current;
    if (!rsz) return;
    const onDown = (e) => {
      if (e.button !== 0 || isMobile()) return; // no resize on mobile
      e.preventDefault();
      e.stopPropagation();
      const { w, h } = sizeRef.current;
      stateRef.current = { type: 'resize', startX: e.clientX, startY: e.clientY, origW: w, origH: h };
      setIsResizing(true);
      window.addEventListener('pointermove', onWindowMove);
      window.addEventListener('pointerup',   onWindowUp);
    };
    rsz.addEventListener('pointerdown', onDown);
    return () => rsz.removeEventListener('pointerdown', onDown);
  }, [onWindowMove, onWindowUp]);

  return { panelRef, headerRef, resizeRef, pos, size, isDragging, isResizing };
}

let _z = 10;
function nextZ() { return ++_z; }
